import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  MenuItem,
  Grid,
  Box,
  Backdrop,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ThemedTabs from "../components/ThemedTabs";
import ThemedSelect from "../components/ThemedSelect";
import ThemedTextField from "../components/ThemedTextField";
import ThemedDatePicker from "../components/ThemedDatePicker";
import ThemedFormControlLabel from "../components/ThemedFormControlLabel";
import ThemedTypography from "../components/ThemedTypography";
import MobileFileList from "../components/MobileFileList";
import MobileMaterialsTab from "../components/MobileMaterialsTab";
import MobileLaborTab from "../components/MobileLaborTab";
import { PROJECT_STATUSES, DARK_THEME_BODY_COLOR } from "../constants";
import { decoratedFetch } from "../req_utils";
import {
  createAutogeneratedLog,
  createProjectAssignmentLog,
} from "../log_utils";

dayjs.extend(utc);

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ height: "100%" }}
      {...other}
    >
      {value === index && <Box sx={{ p: 0, height: "100%" }}>{children}</Box>}
    </div>
  );
}

const MobileProjectDetailsTabs = ({
  project: projectProp,
  employees,
  setSnackbarMessage,
  onProjectUpdate,
}) => {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const employee = useSelector((state) => state.employee);
  const [project, setProject] = useState(projectProp);
  const [tabValue, setTabValue] = useState(0);
  const [backdropOpen, setBackdropOpen] = useState(true);
  const [branchAddresses, setBranchAddresses] = useState([]);
  const [localProjectName, setLocalProjectName] = useState(
    projectProp?.name || "",
  );
  const [localProjectNotes, setLocalProjectNotes] = useState(
    projectProp?.project_notes || "",
  );

  useEffect(() => {
    if (projectProp) {
      setBackdropOpen(false);
      setProject(projectProp);
      setLocalProjectName(projectProp.name || "");
      setLocalProjectNotes(projectProp.project_notes || "");
    }
  }, [projectProp]);

  useEffect(() => {
    decoratedFetch("/list_branch_addresses")
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setBranchAddresses(data);
        } else {
          setBranchAddresses([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching branch addresses: ", error);
        setBranchAddresses([]);
      });
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const updateProject = (fieldName, fieldObj, newProject) => {
    let body;
    if (fieldName.endsWith("_id")) {
      body = { [fieldName]: fieldObj.id };
    } else {
      body = { [fieldName]: fieldObj };
    }
    if (fieldName === "sales_employee_id") {
      body.sales_employee_first_name = fieldObj.first_name;
      body.sales_employee_last_name = fieldObj.last_name;
    }
    decoratedFetch(`/update_project/${newProject.id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (response.status === 200) {
          if (onProjectUpdate) {
            let updatedProject = { ...newProject };
            if (fieldName === "status_id") {
              updatedProject = { ...updatedProject, status: fieldObj };
            } else if (fieldName === "assignee_id") {
              updatedProject = { ...updatedProject, assignee: fieldObj };
            } else if (fieldName === "name") {
              updatedProject = { ...updatedProject, name: fieldObj };
            }
            onProjectUpdate(updatedProject);
          }
          return;
        }
        throw new Error("Error message generated");
      })
      .then(() => {
        setSnackbarMessage("Project saved");
        if (fieldName === "status_id") {
          createAutogeneratedLog(
            newProject.id,
            employee.id,
            `Project status changed to \"${fieldObj.name}\" by ${employee.first_name} ${employee.last_name}`,
          )
            .then((response) => {
              if (response.status !== 201) {
                throw new Error("Error occurred");
              }
            })
            .catch((error) => {
              console.error(`Error creating log: ${error}`);
            });
        } else if (fieldName === "assignee_id") {
          createProjectAssignmentLog(
            newProject,
            employee.id,
            `${employee.first_name} ${employee.last_name} assigned project to ${fieldObj.first_name} ${fieldObj.last_name}`,
          )
            .then((response) => {
              if (response.status !== 201) {
                throw new Error("Error occurred");
              }
            })
            .catch((error) => {
              console.error(`Error creating log: ${error}`);
            });
        }
      })
      .catch((error) => {
        console.error("Error saving project", error);
        setSnackbarMessage("Failed to save project");
      });
  };

  const handleStatusChange = (index) => {
    const newStatus = { id: index + 1, name: PROJECT_STATUSES[index] };
    if (newStatus.id === project?.status?.id) {
      return;
    }
    const newProject = { ...project, status: newStatus };
    updateProject("status_id", newStatus, newProject); // Need to pass the whole status object to generate the log from its name
    setProject(newProject);
  };

  const handleAssigneeChange = (event) => {
    const newAssigneeId = event.target.value;
    if (newAssigneeId === project?.assignee?.id) {
      return;
    }
    const newAssignee = employees.find(
      (employee) => employee.id === newAssigneeId,
    );
    const newProject = { ...project, assignee: newAssignee };
    updateProject("assignee_id", newAssignee, newProject); // Need to pass the whole employee object to generate the log from their name
    setProject(newProject);
  };

  const handleSalesEmployeeChange = (event) => {
    const newSalesRepId = event.target.value;
    if (newSalesRepId === project?.sales_employee?.id) {
      return;
    }
    const newSalesRep = employees.find(
      (employee) => employee.id === newSalesRepId,
    );
    const newProject = { ...project, sales_employee: newSalesRep };
    updateProject("sales_employee_id", newSalesRep, newProject); // Need to pass the whole employee object to generate the log from their name
    setProject(newProject);
  };

  const handleProjectNameChange = (event) => {
    setLocalProjectName(event.target.value);
  };

  const handleProjectNameBlur = () => {
    if (localProjectName !== project?.name) {
      const newProject = { ...project, name: localProjectName };
      updateProject("name", localProjectName, newProject);
      setProject(newProject);
    }
  };

  const handleBranchChange = (event) => {
    const newBranchAddressId = event.target.value;
    if (newBranchAddressId === project?.branch?.id) {
      return;
    }
    const newBranchAddress = branchAddresses.find(
      (branchAddress) => branchAddress.id === newBranchAddressId,
    );
    const newProject = { ...project, branch: newBranchAddress };
    updateProject("branch_id", newBranchAddress, newProject);
    setProject(newProject);
  };

  const handlePMChange = (event) => {
    const newPMId = event.target.value;
    if (newPMId === project?.pm_employee?.id) {
      return;
    }
    const newPM = employees.find((employee) => employee.id === newPMId);
    const newProject = { ...project, pm_employee: newPM };
    updateProject("pm_employee_id", newPM, newProject);
    setProject(newProject);
  };

  const handleMeasuredByChange = (event) => {
    const newMeasuredById = event.target.value;
    if (newMeasuredById === project?.measured_by_employee?.id) {
      return;
    }
    const newMeasuredBy = employees.find(
      (employee) => employee.id === newMeasuredById,
    );
    const newProject = { ...project, measured_by_employee: newMeasuredBy };
    updateProject("measured_by_employee_id", newMeasuredBy, newProject);
    setProject(newProject);
  };

  const handleStartDateAccept = (newStartDate) => {
    // Only save if the value has changed
    const currentDate = project?.project_start_date
      ? dayjs.utc(project.project_start_date)
      : null;
    const newDate = newStartDate ? dayjs.utc(newStartDate) : null;

    if (!currentDate && !newDate) return; // Both are null/undefined
    if (currentDate && newDate && currentDate.isSame(newDate)) return; // Same date

    const newProject = { ...project, project_start_date: newStartDate };
    updateProject("project_start_date", newStartDate, newProject);
    setProject(newProject);
  };

  const handleCompletionDateAccept = (newCompletionDate) => {
    // Only save if the value has changed
    const currentDate = project?.project_completion_date
      ? dayjs.utc(project.project_completion_date)
      : null;
    const newDate = newCompletionDate ? dayjs.utc(newCompletionDate) : null;

    if (!currentDate && !newDate) return; // Both are null/undefined
    if (currentDate && newDate && currentDate.isSame(newDate)) return; // Same date

    const newProject = { ...project, project_completion_date: newCompletionDate };
    updateProject("project_completion_date", newCompletionDate, newProject);
    setProject(newProject);
  };

  const handleContractTypeChange = (event) => {
    const newContractType = event.target.value;
    const newProject = { ...project, contract_type: newContractType };
    updateProject("contract_type", newContractType, newProject);
    setProject(newProject);
  };

  const handlePaymentPerformanceBondChange = (event) => {
    const newValue = event.target.checked;
    const newProject = { ...project, payment_performance_bond: newValue };
    updateProject("payment_performance_bond", newValue, newProject);
    setProject(newProject);
  };

  const handleCertifiedPayrollChange = (event) => {
    const newValue = event.target.checked;
    const newProject = { ...project, certified_payroll: newValue };
    updateProject("certified_payroll", newValue, newProject);
    setProject(newProject);
  };

  const handleCommissionsPaidChange = (event) => {
    const newValue = event.target.checked;
    const newProject = { ...project, commissions_paid: newValue };
    updateProject("commissions_paid", newValue, newProject);
    setProject(newProject);
  };

  const handleNotesChange = (event) => {
    setLocalProjectNotes(event.target.value);
  };

  const handleNotesBlur = () => {
    if (localProjectNotes !== project?.project_notes) {
      const newProject = { ...project, project_notes: localProjectNotes };
      updateProject("project_notes", localProjectNotes, newProject);
    }
  };

  const dateTheme = React.useMemo(
    () =>
      createTheme({
        components: {
          MuiInputLabel: {
            styleOverrides: {
              root: {
                marginTop: project?.project_completion_date ? "0px" : "-7px",
              },
            },
          },
        },
      }),
    [project],
  );

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={backdropOpen}
        onClick={() => setBackdropOpen(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <ThemedTabs
        value={tabValue}
        onChange={handleTabChange}
        labels={["Details", "Materials", "Labor", "Files", "Notes"]}
        aria-label="project-details-tabs"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: darkThemeEnabled ? DARK_THEME_BODY_COLOR : "#f7f7f7",
        }}
      />
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <TabPanel value={tabValue} index={0} sx={{ height: "100%" }}>
          <Grid container sx={{ py: "20px", px: "15px" }}>
            <Grid item xs={12}>
              <ThemedTypography>
                <strong>{"Account: "}</strong>
                {project?.account?.name}
              </ThemedTypography>
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedTextField
                label="Project Name"
                fullWidth
                size="small"
                variant="outlined"
                value={localProjectName}
                onChange={handleProjectNameChange}
                onBlur={handleProjectNameBlur}
                InputLabelProps={{ shrink: localProjectName?.length > 0 }}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedSelect
                id="status-select"
                value={project?.status?.id ? project.status?.id - 1 : ""}
                onChange={(e) => handleStatusChange(e.target.value)}
                label="Status"
                size="small"
                fullWidth
                renderValue={(selectedId) => PROJECT_STATUSES[selectedId]}
                MenuProps={{ disableScrollLock: true }}
              >
                {PROJECT_STATUSES.map((proj_status, i) => (
                  <MenuItem key={i} value={i}>
                    {proj_status}
                  </MenuItem>
                ))}
              </ThemedSelect>
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedSelect
                label="Assignee"
                value={project?.assignee ? project.assignee?.id : ""}
                onChange={handleAssigneeChange}
                size="small"
                fullWidth
                renderValue={(selectedId) => {
                  const selected = employees.find((e) => e.id === selectedId);
                  return selected
                    ? `${selected.first_name} ${selected.last_name}`
                    : "";
                }}
                MenuProps={{ disableScrollLock: true }}
                sx={{ py: 0 }}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name}
                  </MenuItem>
                ))}
              </ThemedSelect>
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedSelect
                label="Sales Rep"
                value={
                  project?.sales_employee ? project.sales_employee?.id : ""
                }
                onChange={handleSalesEmployeeChange}
                size="small"
                fullWidth
                renderValue={(selectedId) => {
                  const selected = employees.find((e) => e.id === selectedId);
                  return selected
                    ? `${selected.first_name} ${selected.last_name}`
                    : "";
                }}
                MenuProps={{ disableScrollLock: true }}
                sx={{ py: 0 }}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name}
                  </MenuItem>
                ))}
              </ThemedSelect>
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedSelect
                id="branch-select"
                size="small"
                value={project?.branch?.id || ""}
                onChange={handleBranchChange}
                label="Genesis Branch Address"
                fullWidth
                renderValue={(selectedId) => {
                  const selected = branchAddresses.find(
                    (branchAddress) => branchAddress.id === selectedId,
                  );
                  return selected
                    ? `${selected.line_1}, ${selected.city} ${selected.state} ${selected.zip}`
                    : "";
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                {branchAddresses.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.line_1}, {branch.city} {branch.state} {branch.zip}
                  </MenuItem>
                ))}
              </ThemedSelect>
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedSelect
                id="pm-select"
                value={project?.pm_employee ? project.pm_employee?.id : ""}
                onChange={handlePMChange}
                label="Project Manager"
                fullWidth
                size="small"
                renderValue={(selectedId) => {
                  const selected = employees.find((e) => e.id === selectedId);
                  return selected
                    ? `${selected.first_name} ${selected.last_name}`
                    : "";
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name}
                  </MenuItem>
                ))}
              </ThemedSelect>
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedSelect
                id="mb-select"
                value={project?.measured_by_employee?.id || ""}
                size="small"
                onChange={handleMeasuredByChange}
                label="Measured By"
                fullWidth
                renderValue={(selectedId) => {
                  const selected = employees.find((e) => e.id === selectedId);
                  return selected
                    ? `${selected.first_name} ${selected.last_name}`
                    : "";
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                {employees
                  .filter((employee) => employee.enabled)
                  .map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.first_name} {employee.last_name}
                    </MenuItem>
                  ))}
              </ThemedSelect>
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemeProvider theme={dateTheme}>
                <ThemedDatePicker
                  label="Project Start Date"
                  value={
                    project?.project_start_date
                      ? dayjs.utc(project?.project_start_date)
                      : null
                  }
                  size="small"
                  onAccept={handleStartDateAccept}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 0px 8.5px 14px",
                      width: "100%",
                    },
                    width: "100%",
                  }}
                />
              </ThemeProvider>
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemeProvider theme={dateTheme}>
                <ThemedDatePicker
                  label="Project Completion Date"
                  value={
                    project?.project_completion_date
                      ? dayjs.utc(project?.project_completion_date)
                      : null
                  }
                  size="small"
                  onAccept={handleCompletionDateAccept}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 0px 8.5px 14px",
                      width: "100%",
                    },
                    width: "100%",
                  }}
                />
              </ThemeProvider>
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <ThemedSelect
                id="contract-type-select"
                value={project?.contract_type || ""}
                onChange={handleContractTypeChange}
                label="Contract Type"
                size="small"
                fullWidth
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="general_contract">General Contract</MenuItem>
                <MenuItem value="subcontract">Subcontract</MenuItem>
              </ThemedSelect>
            </Grid>
            <Grid item xs={6} sx={{ mt: "20px" }}>
              <ThemedFormControlLabel
                control={
                  <Checkbox
                    checked={project?.payment_performance_bond || false}
                    onChange={handlePaymentPerformanceBondChange}
                    color="primary"
                  />
                }
                label="Payment + Performance Bond"
              />
            </Grid>
            <Grid item xs={6} sx={{ mt: "20px" }}>
              <ThemedFormControlLabel
                control={
                  <Checkbox
                    checked={project?.certified_payroll || false}
                    onChange={handleCertifiedPayrollChange}
                    color="primary"
                  />
                }
                label="Certified Payroll"
              />
            </Grid>
            <Grid item xs={6} sx={{ mt: "20px" }}>
              <ThemedFormControlLabel
                control={
                  <Checkbox
                    checked={project?.commissions_paid || false}
                    onChange={handleCommissionsPaidChange}
                    color="primary"
                  />
                }
                label="Commissions Paid"
              />
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <MobileMaterialsTab
            open={tabValue === 1}
            projectId={projectProp?.id}
            setSnackbarMessage={setSnackbarMessage}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <MobileLaborTab
            open={tabValue === 2}
            projectId={projectProp?.id}
            setSnackbarMessage={setSnackbarMessage}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <MobileFileList open={tabValue === 3} projectId={projectProp?.id} />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <Grid container sx={{ py: "20px", px: "15px" }}>
            <Grid item xs={12}>
              <ThemedTextField
                label="Notes"
                fullWidth
                variant="outlined"
                value={localProjectNotes}
                onChange={handleNotesChange}
                onBlur={handleNotesBlur}
                multiline
                rows={20}
                size="small"
              />
            </Grid>
          </Grid>
        </TabPanel>
      </Box>
    </Box>
  );
};

export default MobileProjectDetailsTabs;
